// FT-199 STEP-10: чистые правила карточки операции Ren в чате — статусы, опрос, группа «Выполнить все».
// Компонент (AiActionCard.vue) и AiAssistant.vue только вызывают их; здесь нет ни Vue, ни сети.
//
// Контракт api (CTR-03): элемент беседы `role: "action"` несёт
//   operation: { id, tool, status, error_code, effect, result_ui_url, expires_at }
// и приходит только при запросе с `capabilities=action_cards`.

import { outcomeForStatus } from "@/components/agents/agentOperationOutcome";

export const CAPABILITIES = ["action_cards"];
// Один запрос пачки — не больше MAX_BATCH операций (AgentOperationsController::MAX_BATCH).
const MAX_BATCH = 50;

const PENDING = "pending_approval";

// Статус операции → подпись и цвет чипа. Набор закрыт контрактом; неизвестный статус (новый на бэкенде)
// деградирует в общую подпись, а не в голую строку ключа.
// in_progress (исполнитель уже взял строку) для пользователя то же, что accepted: «выполняется».
const ACCEPTED_META = { color: "blue", key: "ai_assistant.action_card.status_accepted" };
export const STATUS_META = {
  pending_approval: { color: "orange", key: "ai_assistant.action_card.status_pending_approval" },
  accepted: ACCEPTED_META,
  in_progress: ACCEPTED_META,
  done: { color: "success", key: "ai_assistant.action_card.status_done" },
  rejected: { color: "grey", key: "ai_assistant.action_card.status_rejected" },
  expired: { color: "grey", key: "ai_assistant.action_card.status_expired" },
  failed: { color: "error", key: "ai_assistant.action_card.status_failed" },
  stale: { color: "warning", key: "ai_assistant.action_card.status_stale" },
  unavailable: { color: "grey", key: "ai_assistant.action_card.status_unavailable" },
};
export const STATUS_UNKNOWN = { color: "grey", key: "ai_assistant.action_card.status_unknown" };

// Пояснение под итогом — для статусов, где пользователю нужно знать, что делать дальше.
export const STATUS_NOTE_KEYS = {
  accepted: "ai_assistant.action_card.note_accepted",
  in_progress: "ai_assistant.action_card.note_accepted",
  stale: "ai_assistant.action_card.note_stale",
  expired: "ai_assistant.action_card.note_expired",
  unavailable: "ai_assistant.action_card.note_unavailable",
};

// failed с этими кодами: деньги могли уйти, а запись о них не подтверждена — повторять нельзя.
export const OUTCOME_UNKNOWN_CODES = ["taxi_outcome_unknown", "taxi_transaction_not_recorded"];
export const OUTCOME_UNKNOWN_KEY = "ai_assistant.action_card.outcome_unknown";
// Операция выполняется асинхронно — её статус перечитываем, пока он не конечный.
// in_progress — статус строки внутри исполнителя; в карточку он попадает редко, но он такой же нефинальный
// (и показывается как accepted — STATUS_META, STATUS_NOTE_KEYS).
const POLLABLE_STATUSES = ["accepted", "in_progress"];
export const FAST_POLL_MS = 5000;
export const SLOW_POLL_MS = 30000;
export const FAST_POLL_WINDOW_MS = 2 * 60 * 1000;

export function isPollable(status) {
  return POLLABLE_STATUSES.includes(status);
}

// Задержка до следующего перечитывания; elapsed — сколько прошло с начала опроса.
export function pollDelay(elapsedMs) {
  return elapsedMs < FAST_POLL_WINDOW_MS ? FAST_POLL_MS : SLOW_POLL_MS;
}

export function statusMeta(status) {
  return STATUS_META[status] || STATUS_UNKNOWN;
}

export function isOutcomeUnknown(operation) {
  return !!operation && operation.status === "failed" && OUTCOME_UNKNOWN_CODES.includes(operation.error_code);
}

// Текст под провалом: неизвестный исход — предупреждение «не повторять»; иначе причина из словаря экрана «Ожидают
// одобрения» (agent_operations.failure_*): те же коды, те же тексты.
export function failureKey(operation) {
  if (isOutcomeUnknown(operation)) return OUTCOME_UNKNOWN_KEY;
  return outcomeForStatus("failed", operation && operation.error_code).key;
}

// Кнопки «Выполнить» / «Отмена» — только пока операция ждёт решения.
export function canDecide(operation) {
  return !!operation && operation.status === PENDING;
}

// Ссылка «Открыть» на результат — готовые атрибуты v-btn. Внутренний путь — в роутер; внешний — только http(s),
// в новой вкладке.
// Любая другая схема (javascript:, data:) — не ссылка: адрес приходит с сервера, но ссылкой управляет не пользователь.
export function resultLink(url) {
  if (typeof url !== "string" || url === "") return null;
  if (url.startsWith("/") && !url.startsWith("//")) return { to: url };
  if (/^https?:\/\//i.test(url)) return { href: url, target: "_blank", rel: "noopener noreferrer" };
  return null;
}

const isAction = (message) => !!message && message.role === "action" && !!message.operation;

// Ждущие решения карточки последнего хода (после последнего сообщения пользователя) — кандидаты на «Выполнить все».
// Группа есть только из двух и больше: одну карточку решают её собственные кнопки. Пачка — не больше MAX_BATCH.
export function pendingBatch(messages) {
  const list = Array.isArray(messages) ? messages : [];
  let start = 0;
  for (let i = list.length - 1; i >= 0; i -= 1) {
    if (list[i] && list[i].role === "user") { start = i + 1; break; }
  }
  const pending = list.slice(start).filter((m) => isAction(m) && m.operation.status === PENDING);
  if (pending.length < 2) return { ids: [], firstMessageId: null };
  const batch = pending.slice(0, MAX_BATCH);
  return { ids: batch.map((m) => m.operation.id), firstMessageId: batch[0].id };
}

// Результат пачки (200 { data: [{ id, status, error }] }) → сколько решений состоялось.
export function batchDecidedCount(results) {
  return (Array.isArray(results) ? results : []).filter((entry) => entry && !entry.error).length;
}
